import { CanvasConfigV2 } from '@editor/interfaces/editor'
import { useBlueprintEditorContext } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import { BlueprintEditorTools, Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import usePointSelection from '@editor/pages/projects/blueprint-editor/hooks/usePointSelection'
import { setSelectedTool } from '@editor/pages/projects/blueprint-editor/store/blueprintEditor'
import { focusCamera } from '@editor/services/Editor'
import { RootState, useAppDispatch } from '@editor/store/app'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'
import { Vector3 } from 'three'

import { setTarget } from '../store'

export default function useMainCanvas(): CanvasConfigV2 {
  // Context
  const { arcballControls, perspectiveCamera } = useBlueprintEditorContext()

  // Hooks
  const { getIntersection } = usePointSelection()

  // Store
  const dispatch = useAppDispatch()
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const prevSelectedTool = useSelector((state: RootState) => state.blueprint.editor.prevSelectedTool)
  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)

  // Derived
  const isToolSelected = selectedTool === BlueprintEditorTools.Focus
  const is3DMode = mode === Mode.Editor3D

  return {
    events: {
      onRawMouseUp: (event) => {
        const point = getIntersection(event)
        if (!point || !arcballControls.current || !perspectiveCamera || !is3DMode) return

        // Possible action to trigger
        // - Mouse middle click at anytime, with any tool
        if (event.button === 1) {
          focusCamera(arcballControls.current, perspectiveCamera.current, new Vector3(...point), Infinity)
          dispatch(setTarget(point))

          mixpanel.track('Camera Focus', {
            Trigger: 'Middle Click (Any Tool)',
          })
        }
      },
      onMouseUp: (event, { isDoubleClicked }) => {
        if (!is3DMode) return

        const point = getIntersection(event)
        if (!point || !arcballControls.current) return

        // Possible action to trigger
        // - Double click on the same point with only the MOVE tool
        // - FOCUS tool is selected
        if (
          (event.target instanceof HTMLCanvasElement &&
            isDoubleClicked &&
            selectedTool === BlueprintEditorTools.Selector) ||
          isToolSelected
        ) {
          focusCamera(arcballControls.current, perspectiveCamera.current, new Vector3(...point), Infinity)
          dispatch(setTarget(point))

          mixpanel.track('Camera Focus', {
            Trigger: isToolSelected ? 'Single Click (Focus Tool)' : 'Double Click (Select Tool)',
          })

          if (isToolSelected && prevSelectedTool) {
            dispatch(setSelectedTool(prevSelectedTool))
          }
        }
      },
    },
  }
}
